/**
 * Mixpanel identity management.
 *
 * The Mixpanel library is loaded as a global script in index.html and ONLY on
 * the production host (app.global-bridge.net). On localhost/staging
 * `window.mixpanel` is undefined, so every call here is guarded and becomes a
 * no-op off-prod.
 *
 * Why this exists: autocapture assigns each device/session a random
 * `distinct_id`, so without an `identify()` call every visitor shows up as a
 * separate anonymous user. Calling `identify()` on login merges that
 * anonymous activity into the real, stable user id (Mixpanel's "ID Merge"),
 * so one person is resolved across devices and sessions instead of many
 * anonymous IDs.
 */

/** Minimal user shape needed to identify a person in Mixpanel. */
export interface MixpanelUser {
  /** Stable app user id (Prisma UUID) — used as the Mixpanel distinct_id. */
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  brandId: number | null;
  brandName: string | null;
}

/**
 * Resolve the current device's anonymous activity to a known user, set their
 * profile properties, and register brand/role as super properties so every
 * subsequent event is filterable by brand and role.
 */
export function identifyMixpanelUser(user: MixpanelUser): void {
  const mixpanel = window.mixpanel;
  if (!mixpanel) return;

  // Merge the anonymous device id into the stable user id.
  mixpanel.identify(user.id);

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

  // Profile properties ($email/$name are Mixpanel's reserved display fields).
  mixpanel.people.set({
    $email: user.email,
    $name: fullName || user.email,
    role: user.role,
    brandId: user.brandId,
    brandName: user.brandName,
  });

  // Super properties — attached to every event sent after this call.
  mixpanel.register({
    role: user.role,
    brandId: user.brandId,
    brandName: user.brandName,
  });
}

/**
 * Clear the identified user on logout so the next person on a shared device
 * is not merged into the previous user's ID cluster.
 */
export function resetMixpanelUser(): void {
  window.mixpanel?.reset();
}
